import type { Deal } from '@/types/deal';
import type { ReferralLinkChannel } from '@/types/referral';

/**
 * Canonical query-param keys for inbound referral URLs. Both mobile (URL
 * parsing in `lib/pendingReferral.ts`, route params in `app/[slug].tsx`)
 * and backend (`PublicSignupsController`, `AuthController`,
 * `ReferralLink#public_url`) agree on this shape:
 *
 *   `?r=<ReferralLink.token>`     — current, tracked, deal-anchored
 *   `?ref=<User.ref_id>`          — legacy, untracked, no deal
 *
 * The share URL itself is built server-side and returned on every
 * ReferralLink response as `public_url`. Mobile callers read `link.public_url`
 * directly — never reconstruct the URL from `token`. The backend honors
 * `ENV['APP_BASE_URL']` so dev returns dev URLs, prod returns prod URLs.
 */
export const REFERRAL_TOKEN_PARAM = 'r';
export const REFERRAL_LEGACY_REF_ID_PARAM = 'ref';

/**
 * App-invite share message (the generic link). Leads with the invitee's
 * side of the deal — they're the one reading it. Amounts mirror
 * `Referral::INVITE_*_CENTS` on the backend; update together.
 */
export function buildInviteMessage(): string {
  return "I'm earning real cash on MarketPryce Rewards. Sign up with my link and you get an extra $10 when you finish your first deal:";
}

/**
 * Brand-aware share message. Falls back to a generic line when no deal
 * is selected (the user is sharing from "All deals" mode).
 */
export function buildShareMessage(deal: Deal | null): string {
  if (!deal) {
    return 'I just started earning cash on MarketPryce. Sign up with my link and we both win:';
  }
  return `I just earned cash with ${deal.display_name} on MarketPryce. Sign up with my link and we both win:`;
}

/**
 * Channel-tinted share message variants. Each channel gets a phrasing
 * that fits its native pattern (Story/DM, bio link, word-of-mouth) while
 * keeping the brand name interpolated when a deal is selected. Falls
 * back to `buildShareMessage` for `text`, `email`, `copy`, and `other`.
 */
export function buildChannelMessage(
  channel: ReferralLinkChannel,
  deal: Deal | null,
): string {
  const brandPart = deal ? ` with ${deal.display_name}` : '';
  switch (channel) {
    case 'instagram':
      return `Follow my link${brandPart} on MarketPryce — sign up and we both win:`;
    case 'tiktok':
      return `Use my link${brandPart} on MarketPryce — sign up and we both win:`;
    case 'campus':
      return `My MarketPryce link${brandPart} — sign up and we both win:`;
    default:
      return buildShareMessage(deal);
  }
}
